// src/redux/TrainAndContentSlice.js

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import TrainAndContent from "../../services/api/trainAndContentService";
import axios from "axios";
import { flattenObject, unauthorizedError } from "../../utils/helpers";
import { API_URL } from "../../utils/constants";
import AppSecureStorage from "../../services/secureStorage";
import { setSessionExpired, setTotalPage } from "./globalSlice";

const trainAndContent = new TrainAndContent();
const storage = new AppSecureStorage();

export const getAllUploadedDocuments = createAsyncThunk(
  "/admin/train/content?page=1&limit=1",
  async ({ page, limit, type, navigate, searchQuery }, { dispatch }) => {
    try {
      const response = await trainAndContent.getAllDocuments(
        page,
        limit,
        type,
        navigate,
        searchQuery
      );
      if (response) {
        dispatch(setTotalPage(response?.total));
        dispatch(setDocumentData(response));
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  }
);

export const getAllUploadedDocumentsProfile = createAsyncThunk(
  "/admin/train/content?page=1",
  async ({ page, limit, navigate, searchQuery }, { dispatch }) => {
    dispatch(setIsDocumentLoad(true));
    try {
      const response = await trainAndContent.getAllDocumentsProfile(
        page,
        limit,
        navigate,
        searchQuery
      );
      if (response) {
        dispatch(setIsDocumentLoad(false));
        dispatch(setTotalPage(response?.total));
        return response;
      } else {
        dispatch(setIsDocumentLoad(false));
        throw new Error();
      }
    } catch (error) {
      dispatch(setIsDocumentLoad(false));
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  }
);

export const uploadTrainingData = createAsyncThunk(
  "admin/uploadTrainingData",
  async (
    { body, fileType, page, limit, navigate, searchQuery },
    { dispatch }
  ) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/v1/admin/train/content/${fileType}`,
        body,
        {
          headers: {
            Authorization: `Bearer ${storage.get("token")}`,
          },
        }
      );

      if (response) {
        toast.success(
          `Processing has started for ${fileType} upload. Once the processing is complete, you will be notified, and you can ask your queries to the bot.`
        );
        dispatch(
          getAllUploadedDocuments({
            page: page,
            limit: limit,
            type: fileType,
            searchQuery: searchQuery,
          })
        );

        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.response?.data?.errors
            ? error?.response?.data?.errors
            : error?.response?.data?.message
        );
      }
      throw error;
    }
  }
);

export const deleteContents = createAsyncThunk(
  "/admin/train/contents/delete",
  async (
    { fileIds, page, limit, fileType, navigate, searchQuery },
    { dispatch }
  ) => {
    try {
      const response = await trainAndContent.deleteContents({
        fileIds,
        navigate,
      });
      // toast.success("Record deleted successfully");
      dispatch(
        getAllUploadedDocuments({
          page: page,
          limit: limit,
          type: fileType,
          searchQuery: searchQuery,
        })
      );
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.response?.data?.message
            ? error?.response?.data?.message
            : error?.data?.errors
        );
      }
      throw error;
    }
  }
);

export const updateContentRoles = createAsyncThunk(
  "/admin/train/content/update-roles/:contentId",
  async (
    { contentId, roleIds, page, limit, fileType, navigate, searchQuery },
    { dispatch }
  ) => {
    try {
      const response = await trainAndContent.updateContentRoles(
        contentId,
        roleIds,
        navigate
      );
      dispatch(
        getAllUploadedDocuments({
          page: page,
          limit: limit,
          type: fileType,
          navigate,
          searchQuery: searchQuery || "",
        })
      );
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.response?.data?.message
            ? error?.response?.data?.message
            : error?.data?.errors
        );
      }
      throw error;
    }
  }
);

export const getAllCustom_data = createAsyncThunk(
  "/custom-action",
  async ({ page, limit, navigate }, { dispatch }) => {
    try {
      const response = await trainAndContent.getAllCustomData(
        page,
        limit,
        navigate
      );
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error("Something went wrong");
      }
      throw error;
    }
  }
);

export const getAllCustom_Action = createAsyncThunk(
  "/api/v1/action",
  async ({ navigate, searchQuery }, { dispatch }) => {
    try {
      const response = await trainAndContent.getCustomAction(
        navigate,
        searchQuery
      );
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error("Something went wrong");
      }
      throw error;
    }
  }
);

export const removeCustom_data = createAsyncThunk(
  "/admin/train/custom-action/:id",
  async ({ body, userId, page, limit, navigate }, { dispatch }) => {
    try {
      const response = await trainAndContent.removeCustomData(
        body,
        userId,
        navigate
      );
      toast.success("Record deleted successfully");
      dispatch(getAllCustom_data({ page, limit }));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.message ? error?.data?.message : error?.data?.errors
        );
      }
      throw error;
    }
  }
);

export const updateCustom_data = createAsyncThunk(
  "/train/custom-action/:id",
  async ({ id, body, page, limit, navigate }, { dispatch }) => {
    try {
      const response = await trainAndContent.updateCustomData({
        id,
        body,
        navigate,
      });
      toast.success("record Update Successfully");
      dispatch(getAllCustom_data({ page, limit }));
      return response;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.message ? error?.data?.message : error?.data?.errors
        );
      }
      throw error;
    }
  }
);

export const createCustom_data = createAsyncThunk(
  "/train/custom-action",
  async (body, navigate, { dispatch }) => {
    try {
      const response = await trainAndContent.createCustomData(body, navigate);
      toast.success("record create Successfully");
      dispatch(setCustomActionId(response?._id));
      dispatch(setOpenTestingApi(true));

      return response;
    } catch (error) {
      if (error?.response) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error(
          error?.data?.message ? error?.data?.message : error?.data?.errors
        );
      }
      throw error;
    }
  }
);

export const createCustomAPI = createAsyncThunk(
  "/train/createCustomAPI",
  async (requestData, { dispatch }) => {
    const { body, method, headers, url, navigate } = requestData;
    try {
      const response = await axios(url, {
        method: method,
        headers: typeof headers === "string" ? JSON.parse(headers) : headers,
        data: body,
      });
      let flattenObjectData = flattenObject(response.data);
      dispatch(setAPIResponseState(true));
      const apiReponseKeyData = Object.keys(flattenObjectData);
      dispatch(setAPIResponseData(apiReponseKeyData));
      toast.success("API request successful");
      return response.data;
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error("Something went wrong");
      }
      throw error;
    }
  }
);

export const getSingleDataApi = createAsyncThunk(
  "/getSingleDataApi",
  async (id, navigate, { dispatch }) => {
    try {
      const response = await trainAndContent.getSingleData(id, navigate);
      if (response) {
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      } else {
        toast.error("Something went wrong");
      }
      throw error;
    }
  }
);

export const getAllNotificationData = createAsyncThunk(
  "/notification",
  async ({ navigate }, { dispatch }) => {
    try {
      const response = await trainAndContent.getAllNotifications();
      if (response) {
        dispatch(setAllNotifications(response));
        return response;
      } else {
        throw new Error();
      }
    } catch (error) {
      if (error?.response && error?.response.status === 401) {
        unauthorizedError(navigate);
        dispatch(setSessionExpired(true));
      }
      throw error;
    }
  }
);

const TrainAndContentSlice = createSlice({
  name: "TrainAndContect",
  initialState: {
    loading: false,
    allNotifications: null,
    allDocument: null,
    isTestingApiModal: false,
    customActionId: null,
    apiResponseData: null,
    apiResponseState: false,
    selectedTrainStatus: "text",
    documentLoad: false,
    firebaseNotification: { title: "", body: "" },
  },
  reducers: {
    setOpenTestingApi: (state, action) => {
      state.isTestingApiModal = action.payload;
    },
    setAPIResponseData: (state, action) => {
      state.apiResponseData = action.payload;
    },
    setCustomActionId: (state, action) => {
      state.customActionId = action.payload;
    },
    setAPIResponseState: (state, action) => {
      state.apiResponseState = action.payload;
    },
    setDocumentData(state, action) {
      state.allDocument = action.payload;
    },
    setSelectedTrainStatus: (state, action) => {
      state.selectedTrainStatus = action.payload;
    },
    setIsDocumentLoad: (state, action) => {
      state.documentLoad = action.payload;
    },
    setAllNotifications: (state, action) => {
      state.allNotifications = action.payload;
    },
    setFirebaseNotification: (state, action) => {
      state.firebaseNotification = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllUploadedDocumentsProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllUploadedDocumentsProfile.fulfilled, (state, action) => {
        state.loading = false;
        state.allDocument = action.payload;
      })
      .addCase(getAllUploadedDocumentsProfile.rejected, (state) => {
        state.loading = false;
      })
      .addCase(deleteContents.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteContents.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(deleteContents.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateContentRoles.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateContentRoles.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateContentRoles.rejected, (state) => {
        state.loading = false;
      })
      .addCase(uploadTrainingData.pending, (state) => {
        state.loading = true;
      })
      .addCase(uploadTrainingData.fulfilled, (state, action) => {
        state.loading = false;
        state.textDocuments = action.payload;
      })
      .addCase(uploadTrainingData.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllUploadedDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllUploadedDocuments.fulfilled, (state, action) => {
        state.loading = false;
        state.allDocument = action.payload;
      })
      .addCase(getAllUploadedDocuments.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllCustom_data.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllCustom_data.fulfilled, (state, action) => {
        state.loading = false;
        state.renderallCustomData = action.payload;
      })
      .addCase(getAllCustom_data.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllCustom_Action.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllCustom_Action.fulfilled, (state, action) => {
        state.loading = false;
        state.renderallCustomAction = action.payload;
      })
      .addCase(getAllCustom_Action.rejected, (state) => {
        state.loading = false;
      })
      .addCase(removeCustom_data.pending, (state) => {
        state.loading = true;
      })
      .addCase(removeCustom_data.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(removeCustom_data.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateCustom_data.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateCustom_data.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateCustom_data.rejected, (state) => {
        state.loading = false;
      })
      .addCase(createCustom_data.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCustom_data.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createCustom_data.rejected, (state) => {
        state.loading = false;
      })

      .addCase(createCustomAPI.pending, (state) => {
        state.loading = true;
      })
      .addCase(createCustomAPI.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(createCustomAPI.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getSingleDataApi.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSingleDataApi.fulfilled, (state, action) => {
        state.loading = false;
        state.singleDataResponse = action.payload;
      })
      .addCase(getSingleDataApi.rejected, (state) => {
        state.loading = false;
      })
      .addCase(getAllNotificationData.pending, (state) => {
        state.loading = true;
      })
      .addCase(getAllNotificationData.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(getAllNotificationData.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const {
  setOpenTestingApi,
  setCustomActionId,
  setAPIResponseData,
  setAPIResponseState,
  setDocumentData,
  setSelectedTrainStatus,
  setIsDocumentLoad,
  setAllNotifications,
  setFirebaseNotification,
} = TrainAndContentSlice.actions;

export default TrainAndContentSlice.reducer;
